import React, {
	useEffect,
	useMemo,
	useState,
} from 'react';
import {
	Badge,
	Flex,
	Space,
} from 'antd';
import { Link } from 'react-router-dom';
import { LinkOutlined } from '@ant-design/icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft } from '@fortawesome/pro-light-svg-icons';
import { faLink } from '@fortawesome/pro-regular-svg-icons';
import { faArrowRight } from '@fortawesome/pro-thin-svg-icons';
import { Moment } from 'moment';
import classNames from 'classnames';
import {
	ContractCompletionTypes,
	FeatureFlags,
	FixtureTypes,
	PortActionTypes,
	VesselOwnershipTypes,
} from '@shared/utils/constants';
import isItemPortCall from '@shared/utils/isItemPortCall';
import {
	getUtcOffset,
	nowMoment,
	toMoment,
} from '@shared/utils/date';
import { Values } from '@shared/utils/objectEnums';
import { ItineraryPortCallDto } from '@client/screens/estimates/details/helpers/types';
import Select from '@client/components/Select';
import CountryFlag from '@client/components/CountryFlag';
import DatePicker from '@client/components/DatePicker';
import { updateVoyage } from '@client/lib/api';
import showErrorNotification from '@client/utils/showErrorNotification';
import Details from '@client/components/Details';
import { Links } from '@client/utils/links';
import useFeatureFlag from '@client/utils/hooks/useFeatureFlag';
import Button from '@client/components/Button';
import { useAuth } from '@client/lib/auth';
import getFixingCompanyOptions from '@client/utils/getFixingCompanyOptions';
import { useMiscHandlers } from '@client/screens/estimates/details/context/hooks/useMiscHandlers';
import EstimatedActualTag from '@client/components/EstimatedActualTag';
import TooltipIcon from '@client/components/TooltipIcon';
import VesselSelector from '@client/components/VesselSelector';
import { useVoyage } from './VoyageProvider/VoyageProvider';
import { useItinerary } from './ItineraryTab/ItineraryProvider';
import styles from './styles/SelectItineraryEntryAndDates.module.css';

type Props = {
	labelWidth?: number | string;
	columns?: number;
};

const SelectItineraryEntryAndDates = ({
	labelWidth = 300,
	columns,
}: Props) => {
	const [updateLoading, setUpdateLoading] = useState(false);
	const usesTheShip = useFeatureFlag(FeatureFlags.THE_SHIP);
	const {
		itinerary,
		itineraryLoading: loading,
		setItineraryShouldUpdate,
		refreshItinerary,
		setShowCommencementLinking,
	} = useItinerary();

	const {
		voyageDetails,
		refreshDetails,
		fixtureDetails,
		updateVoyageField,
		allVoyages,
		linkTheShip,
		hasCargoRelet,
	} = useVoyage();

	const {
		portOptions
	} = useMiscHandlers();

	const { userIndex, userInfo } = useAuth();

	const onChangeFixingCompany = async (fixingCompanyId: number | null) => {
		try {
			await updateVoyage(voyageDetails.id, { fixingCompanyId });
			refreshDetails();
		} catch (e) {
			showErrorNotification('Could not change Fixing Company', e as Error);
		}
	};

	const fixedById = fixtureDetails.fixedBy ?? null;
	const operatedById = fixtureDetails.operatedBy ?? null;
	const { previousVoyage } = voyageDetails;
	const { nextVoyage } = voyageDetails;

	const filteredVoyages = useMemo(() => {
		if (allVoyages == null) {
			return [];
		}

		return allVoyages.filter((v) => v.id !== voyageDetails.id);
	}, [voyageDetails, allVoyages]);

	const tcInVoyage = useMemo(() => {
		if (!voyageDetails.linkedTcInVoyageId || !filteredVoyages) {
			return null;
		}

		return filteredVoyages.find((v) => v.id === voyageDetails.linkedTcInVoyageId);
	}, [allVoyages, voyageDetails.linkedTcInVoyageId]);

	const [completionType, setCompletionType] = useState<
		ContractCompletionTypes | null
	>(voyageDetails.completionType);

	const portCalls = useMemo(() => {
		return itinerary.filter((entry) => (
			isItemPortCall(entry) && !entry.isCanalTransit
		)) as ItineraryPortCallDto[];
	}, [itinerary]);

	const onSelectEntry = async (
		type: Values<typeof PortActionTypes>,
		portCallId: number | null,
	) => {
		setUpdateLoading(true);

		try {
			let fieldKey: string | null;
			switch (type) {
				case PortActionTypes.DELIVERY:
					fieldKey = 'deliveryPortCallId';
					break;
				case PortActionTypes.REDELIVERY:
					fieldKey = 'redeliveryPortCallId';
					break;
				case PortActionTypes.COMMENCEMENT:
					fieldKey = 'commencementPortCallId';
					break;
				case PortActionTypes.COMPLETION:
					fieldKey = 'completionPortCallId';
					break;
				default:
					fieldKey = null;
					break;
			}

			if (fieldKey == null) {
				return;
			}

			await updateVoyage(voyageDetails.id, {
				[fieldKey]: portCallId ?? null,
			});

			await refreshItinerary();
			refreshDetails();
			setItineraryShouldUpdate(true);
		} catch (e) {
			showErrorNotification('Could not update delivery/redelivery', e as Error);
		} finally {
			setUpdateLoading(false);
		}
	};

	const matchActionWithEntry = useMemo(() => {
		const delivery = portCalls.find((entry) => (
			entry.id === voyageDetails.deliveryPortCallId
		));

		const redelivery = portCalls.find((entry) => (
			entry.id === voyageDetails.redeliveryPortCallId
		));

		const commencement = portCalls.find((entry) => (
			entry.id === voyageDetails.commencementPortCallId
		));

		const completion = portCalls.find((entry) => (
			entry.id === voyageDetails.completionPortCallId
		));

		return {
			commencement,
			delivery,
			redelivery,
			completion,
		};
	}, [portCalls, voyageDetails]);

	const isTcOut = fixtureDetails.type === FixtureTypes.TC_OUT;

	const completionEntry = useMemo(() => {
		if (isTcOut) {
			if (matchActionWithEntry.completion != null) {
				return matchActionWithEntry.completion;
			}

			if (voyageDetails.completionPort != null || voyageDetails.completionDate != null) {
				return undefined;
			}

			return portCalls[portCalls.length - 1];
		}

		if (fixtureDetails.type === FixtureTypes.SPOT) {
			return matchActionWithEntry.completion ?? portCalls[portCalls.length - 1];
		}

		return matchActionWithEntry.redelivery;
	}, [matchActionWithEntry, fixtureDetails.type, isTcOut, portCalls, voyageDetails.completionPort, voyageDetails.completionDate]);

	const isSpot = fixtureDetails.type === FixtureTypes.SPOT;

	const isTcInVoyage = fixtureDetails.type === FixtureTypes.TC_IN;
	const isTcInVessel = voyageDetails.vessel.ownershipType === VesselOwnershipTypes.TC_IN;
	const tcInRedelivery = voyageDetails.completionType === ContractCompletionTypes.TC_IN_DELIVERY;

	const onChangeCompletionType = async (type: ContractCompletionTypes | null) => {
		const currentType = completionType;
		setCompletionType(type);

		try {
			if (
				type === ContractCompletionTypes.TC_IN_DELIVERY &&
				isSpot &&
				voyageDetails.completionPortCallId == null &&
				completionEntry?.id != null
			) {
				await updateVoyage(voyageDetails.id, { completionPortCallId: completionEntry.id });
			}

			await updateVoyage(voyageDetails.id, { completionType: type });

			await refreshItinerary();
			refreshDetails();
			setItineraryShouldUpdate(true);
		} catch (e) {
			setCompletionType(currentType);
			showErrorNotification('Could not update completion type', e as Error);
		}
	};

	const [deliveryDateOffset, setDeliveryDateOffset] = useState<number>(0);
	const [redeliveryDateOffset, setRedeliveryDateOffset] = useState<number>(0);

	useEffect(() => {
		const port = voyageDetails?.deliveryPort;
		const date = voyageDetails?.deliveryDate ?
			toMoment(voyageDetails.deliveryDate).toDate() :
			new Date();

		if (port && port.timezone) {
			const offset = getUtcOffset(port, date);
			setDeliveryDateOffset(offset);
		} else if (voyageDetails?.deliveryDate == null) {
			setDeliveryDateOffset(0);
		} else if (toMoment(voyageDetails.deliveryDate).clone().utcOffset() !== deliveryDateOffset) {
			setDeliveryDateOffset(toMoment(voyageDetails.deliveryDate).clone().utcOffset());
		}
	}, [voyageDetails, setDeliveryDateOffset, deliveryDateOffset]);

	useEffect(() => {
		const port = voyageDetails?.redeliveryPort;
		const date = voyageDetails?.redeliveryDate ?
			toMoment(voyageDetails.redeliveryDate).toDate() :
			new Date();

		if (port && port.timezone) {
			const offset = getUtcOffset(port, date);

			setRedeliveryDateOffset(offset);
		} else if (voyageDetails?.redeliveryDate == null) {
			setRedeliveryDateOffset(0);
		} else if (toMoment(voyageDetails.redeliveryDate).utcOffset() !== redeliveryDateOffset) {
			setRedeliveryDateOffset(toMoment(voyageDetails.redeliveryDate).utcOffset());
		}
	}, [voyageDetails, setRedeliveryDateOffset, redeliveryDateOffset]);

	const deliveryFields = [
		{
			visible: isTcInVoyage,
			key: 'deliveryPort',
			label: 'Delivery Port',
			value: (
				<Select
					showSearch
					placeholder="Select Delivery Port"
					options={portOptions}
					onChange={(port) => updateVoyageField('deliveryPort', port)}
					defaultValue={voyageDetails.deliveryPort?.id}
				/>
			),
		},
		{
			visible: !isTcInVoyage && !isSpot,
			key: 'deliveryPortCall',
			label: (
				<Flex>
					<p style={{ margin: 0, padding: 0 }}>Delivery</p>
					{voyageDetails.showDeliveryBunkersMismatchWarning && (
						<TooltipIcon danger data-testid="delivery-bunkers-mismatch-tooltip">
							BoD and itinerary bunkers do not match correctly
						</TooltipIcon>
					)}
				</Flex>
			),
			value: (
				<Select
					data-testid='delivery-selector'
					placeholder={portCalls.length === 0 ? 'Create a port call first' : 'Select port call'}
					showSearch
					loading={loading || updateLoading}
					key={matchActionWithEntry.delivery?.id}
					defaultValue={matchActionWithEntry.delivery?.id}
					allowClear
					disabled={portCalls.length === 0}
					className={styles.fullWidth}
					onChange={(value) => onSelectEntry(PortActionTypes.DELIVERY, value)}
					optionRender={(option) => (
						<Space
							direction="vertical"
							size={0}
							data-testid={`delivery-select-${option.label}`}
						>
							<Space>
								<CountryFlag countryCode={option.data.country} />
								{option.label}
							</Space>
							{option.data.departureDate && (
								<div style={{ fontSize: 11 }}>
									Departure:
									{toMoment(option.data.departureDate).format('DD/MM/YYYY')}
								</div>
							)}
						</Space>
					)}
					options={portCalls.map((pc) => ({
						value: pc.id,
						label: pc.port.name,
						departureDate: pc.departureDate ?? pc.estimatedDepartureDate,
						country: pc.port.countryCode,
						disabled: matchActionWithEntry.delivery?.id === pc.id,
					}))}
				/>
			),
		},
		{
			visible: !isSpot,
			key: 'deliveryDate',
			label: isTcOut ? 'Est. delivery date' : 'Delivery date',
			value: (
				<Flex gap={5}>
					<EstimatedActualTag estimated={voyageDetails.isDeliveryDateEstimated} />
					<DatePicker
						key={`deliveryDate-${voyageDetails.deliveryDate}`}
						placeholder="Delivery date"
						allowClear
						disabled={voyageDetails.fixture.type !== FixtureTypes.TC_IN}
						className={styles.fullWidth}
						value={voyageDetails?.deliveryDate ? toMoment(voyageDetails.deliveryDate) : null}
						time
						showTimezone
						disableTimezone={voyageDetails.fixture.type !== FixtureTypes.TC_IN}
						onChange={(picked) => {
							if (Array.isArray(picked)) {
								return;
							}

							if (!picked) {
								updateVoyageField('deliveryDate', null);

								return;
							}

							const offMin = voyageDetails.deliveryPort ?
								getUtcOffset(voyageDetails.deliveryPort, toMoment(picked).toDate()) : 0;

							const currentInstant = toMoment(picked).clone().utcOffset(offMin, true);

							setDeliveryDateOffset(offMin);
							updateVoyageField('deliveryDate', currentInstant);
						}}
						onTimezoneChange={(offMin) => {
							if (!Number.isFinite(offMin)) {
								return;
							}

							setDeliveryDateOffset(offMin as number);

							if (voyageDetails.deliveryDate) {
								const currentInstant =
									toMoment(voyageDetails.deliveryDate).clone().utcOffset(offMin as number, true);
								updateVoyageField('deliveryDate', currentInstant);
							}
						}}
						forceOnChange
						forceUtcUpdate
						defaultUtcOffset={
							voyageDetails?.deliveryDate && voyageDetails?.deliveryPort ?
								(getUtcOffset(voyageDetails.deliveryPort,
									toMoment(voyageDetails.deliveryDate).toDate()) ?? 0) :
								(voyageDetails?.deliveryPort ? getUtcOffset(voyageDetails.deliveryPort) : 0)
						}
					/>
				</Flex>
			),
		},

	];

	const redeliveryFields = [
		{
			visible: isTcInVoyage,
			key: 'redeliveryPort',
			label: (<Flex>
				<p style={{ margin: 0, padding: 0 }}>Redelivery port</p>
				{voyageDetails.showRedeliveryBunkersMismatchWarning && (
					<TooltipIcon
						danger
						data-testid="redelivery-bunkers-mismatch-tooltip"
					>
						BoR and itinerary bunkers do not match correctly
					</TooltipIcon>
				)}
			</Flex>),
			value: (
				<Select
					showSearch
					placeholder="Select Redelivery Port"
					options={portOptions}
					value={voyageDetails.redeliveryPort?.id}
					onChange={(portId) => {
						updateVoyageField('redeliveryPort', portId);
					}}
				/>
			),
		},
		{
			visible: !isTcInVoyage && !isSpot,
			key: 'redeliveryPortCall',
			label: (
				<Flex>
					<p style={{ margin: 0, padding: 0 }}>Redelivery</p>
					{voyageDetails.showRedeliveryBunkersMismatchWarning && (
						<TooltipIcon danger data-testid="redelivery-bunkers-mismatch-tooltip">
							BoR and itinerary bunkers do not match correctly
						</TooltipIcon>
					)}
				</Flex>
			),
			value: (
				<Select
					data-testid='redelivery-selector'
					placeholder={portCalls.length === 0 ? 'Create a port call first' : 'Select port call'}
					defaultValue={matchActionWithEntry.redelivery?.id}
					onChange={(value) => onSelectEntry(PortActionTypes.REDELIVERY, value)}
					key={matchActionWithEntry.redelivery?.id}
					className={styles.fullWidth}
					disabled={
						portCalls.length === 0
					}
					loading={loading || updateLoading}
					allowClear
					showSearch
					optionRender={(option) => (
						<Space
							direction="vertical"
							size={0}
							data-testid={`redelivery-select-${option.label}`}
						>
							<Space>
								<CountryFlag countryCode={option.data.country} />
								{option.label}
							</Space>
							{option.data.departureDate && (
								<div style={{ fontSize: 11 }}>
									Departure:
									{toMoment(option.data.departureDate).format('DD/MM/YYYY')}
								</div>
							)}
						</Space>
					)}
					options={portCalls.map((pc) => ({
						value: pc.id,
						label: pc.port.name,
						departureDate: pc.departureDate ?? pc.estimatedDepartureDate,
						country: pc.port.countryCode,
						disabled: matchActionWithEntry.redelivery?.id === pc.id,
					}))}
				/>
			),
		},
		{
			visible: !isSpot,
			key: 'redeliveryDate',
			label: isTcOut ?
				'Est. redelivery date' :
				`Redelivery date ${tcInRedelivery ? '(TC-Out)' : ''}`,
			value: (
				<Flex gap={5}>
					<EstimatedActualTag estimated={voyageDetails.isRedeliveryDateEstimated} />
					<DatePicker
						key={`redeliveryDate-${voyageDetails.redeliveryDate}`}
						className={styles.fullWidth}
						time
						disabled={voyageDetails.fixture.type !== FixtureTypes.TC_IN}
						showTimezone
						value={voyageDetails?.redeliveryDate ? toMoment(voyageDetails.redeliveryDate) : null}
						forceUtcUpdate
						disableTimezone={voyageDetails.fixture.type !== FixtureTypes.TC_IN}
						onChange={(picked) => {
							if (Array.isArray(picked)) {
								return;
							}

							if (!picked) {
								updateVoyageField('redeliveryDate', null);

								return;
							}

							const offMin = voyageDetails.redeliveryPort ?
								getUtcOffset(voyageDetails.redeliveryPort, toMoment(picked).toDate()) : 0;

							const currentInstant = toMoment(picked).clone().utcOffset(offMin, true);

							setRedeliveryDateOffset(offMin);
							updateVoyageField('redeliveryDate', currentInstant);
						}}
						onTimezoneChange={(offMin) => {
							if (!Number.isFinite(offMin)) {
								return;
							}

							setRedeliveryDateOffset(offMin as number);

							if (voyageDetails.redeliveryDate) {
								const currentInstant =
									toMoment(voyageDetails.redeliveryDate).clone().utcOffset(offMin as number, true);
								updateVoyageField('redeliveryDate', currentInstant);
							}
						}}
						defaultUtcOffset={
							voyageDetails?.redeliveryDate && voyageDetails?.redeliveryPort ?
								(getUtcOffset(voyageDetails.redeliveryPort,
									toMoment(voyageDetails.redeliveryDate).toDate()) ?? 0) :
								(voyageDetails?.redeliveryPort ? getUtcOffset(voyageDetails.redeliveryPort) : 0)
						}
					/>
				</Flex>
			),
		},
	];

	const allItems = [
		{
			visible: true,
			key: 'fixingCompany',
			label: 'Fixing Company',
			value: (
				<Select
					data-testid="voyage-fixing-company"
					value={voyageDetails.fixingCompanyId}
					variant="borderless"
					showSearch
					placeholder="Select Fixing Company"
					options={getFixingCompanyOptions(userInfo.fixingCompanies, voyageDetails.fixingCompanyId)}
					onChange={onChangeFixingCompany}
				/>
			),
		},
		{
			visible: !hasCargoRelet,
			key: 'previousVoyage',
			label: (
				<Flex gap={5} align="center">
					<FontAwesomeIcon icon={faArrowLeft} />
					Prev. Contract
					<Button
						type="link"
						icon={(<FontAwesomeIcon size="lg" icon={faLink} />)}
						onClick={() => setShowCommencementLinking(true)}
						data-testid="contract-link-btn"
					/>
				</Flex>
			),
			value: previousVoyage == null ? ' - ' : (
				<Space style={{ padding: 4 }}>
					<Link
						to={Links.Voyage.get(previousVoyage.id)}
						data-testid="previous-voyage-link"
					>
						{previousVoyage?.identifier ?? ' - '}
					</Link>
				</Space>
			),
		},
		{
			visible: !hasCargoRelet,
			key: 'nextVoyage',
			label: (
				<Flex gap={5} align="center">
					Next Contract
					<FontAwesomeIcon icon={faArrowRight} />
				</Flex>
			),
			value: nextVoyage == null ? ' - ' : (
				<Space style={{ padding: 4 }}>
					<Link
						data-testid="next-voyage-link"
						to={Links.Voyage.get(nextVoyage.id)}
					>
						{nextVoyage?.identifier ?? ' - '}
					</Link>
				</Space>
			),
		},
		{
			visible: (isTcInVessel && !isTcInVoyage && !hasCargoRelet && voyageDetails.linkedTcInVoyageId),
			key: 'linkedTcIn',
			label: (
				<Flex vertical>
					Linked TC-in voyage
				</Flex>

			),
			value: (
				<Space style={{ padding: 4 }}>
					<Link to={Links.Voyage.get(voyageDetails.linkedTcInVoyageId!)}>
						{tcInVoyage?.identifier ?? '-'}
					</Link>
				</Space>
			)
		},
		{
			visible: isTcInVessel && !isTcInVoyage && !hasCargoRelet,
			key: 'completionType',
			label: 'Redeliver the vessel on TC IN to the owner',
			value: (
				<Select
					data-testid='redeliver-vessel-selector'
					value={completionType}
					key={completionType}
					onChange={onChangeCompletionType}
					options={[
						{
							label: 'Redeliver the vessel',
							value: ContractCompletionTypes.TC_IN_DELIVERY,
							disabled: voyageDetails.linkedTcInVoyageId == null,
						},
						{ label: 'No', value: ContractCompletionTypes.NEXT_CONTRACT },
					]}
				/>
			),
		},
		{
			visible: hasCargoRelet,
			key: 'vessel',
			label: 'Vessel',
			span: 4,
			value: (
				<VesselSelector
					placeholder="Select vessel"
					value={voyageDetails?.vesselId}
					includeVesselIndex
					includeMarketVessels
					onChange={(vesselId) => updateVoyageField('vesselId', vesselId)}
				/>
			),
		},
		{
			visible: !isTcInVoyage,
			key: '10',
			label: 'Commencement',
			value: (
				<Select
					placeholder={portCalls.length === 0 ? 'Create a port call first' : 'Select port call'}
					showSearch
					loading={loading || updateLoading}
					key={matchActionWithEntry.commencement?.id}
					defaultValue={matchActionWithEntry.commencement?.id}
					allowClear
					disabled
					className={styles.fullWidth}
					onChange={(value) => onSelectEntry(PortActionTypes.COMMENCEMENT, value)}
					optionRender={(option) => (
						<Space direction="vertical" size={0}>
							<Space>
								<CountryFlag countryCode={option.data.country} />
								{option.label}
							</Space>
							{option.data.departureDate && (
								<div style={{ fontSize: 11 }}>
									Departure:
									{toMoment(option.data.departureDate).format('DD/MM/YYYY')}
								</div>
							)}
						</Space>
					)}
					options={portCalls.map((pc) => ({
						value: pc.id,
						label: pc.port.name,
						departureDate: pc.departureDate ?? pc.estimatedDepartureDate,
						country: pc.port.countryCode,
						disabled: matchActionWithEntry.commencement?.id === pc.id,
					}))}
				/>
			),
		},
		{
			visible: isSpot,
			key: '11',
			label: 'Completion',
			value: (
				<Select
					data-testid='completion-selector'
					placeholder={portCalls.length === 0 ? 'Create a port call first' : 'Select port call'}
					defaultValue={completionEntry?.id}
					onChange={(value) => onSelectEntry(PortActionTypes.COMPLETION, value)}
					key={completionEntry?.id}
					className={styles.fullWidth}
					disabled
					loading={loading || updateLoading}
					showSearch
					allowClear
					optionRender={(option) => (
						<Space
							direction="vertical"
							size={0}
							data-testid={`completion-select-${option.label}`}
						>
							<Space>
								<CountryFlag countryCode={option.data.country} />
								{option.label}
							</Space>
							{option.data.departureDate && (
								<div style={{ fontSize: 11 }}>
									Departure:
									{' '}
									{toMoment(option.data.departureDate).format('DD/MM/YYYY')}
								</div>
							)}
						</Space>
					)}
					options={portCalls.map((pc) => ({
						value: pc.id,
						label: pc.port.name,
						departureDate: pc.departureDate ?? pc.estimatedDepartureDate,
						country: pc.port.countryCode,
						disabled: matchActionWithEntry.completion?.id === pc.id,
					}))}
				/>
			),
		},
		{
			visible: !isTcInVoyage,
			key: '1',
			label: 'Commencement date',
			value: (
				<Flex gap={5}>
					<EstimatedActualTag
						data-testid={'estimated-actual-commencement-tag'}
						estimated={voyageDetails.isCommencementDateEstimated}
					/>
					<DatePicker
						key={'commencement-' + voyageDetails.commencementDate?.toISOString()}
						placeholder="Commencement date"
						className={styles.fullWidth}
						defaultValue={voyageDetails?.commencementDate ?? null}
						onChange={(date) => updateVoyageField('commencementDate', date)}
						time
						disabled={!isTcInVoyage}
						forceOnChange
						showTime
						showTimezone
						defaultUtcOffset={voyageDetails.commencementDate?.utcOffset()}
					/>
				</Flex>
			),
		},
		{
			visible: isSpot,
			key: (voyageDetails.completionDate)?.toISOString() ?? 'completionDate',
			label: 'Completion date',
			value: (
				<DatePicker
					key={`CD-${voyageDetails?.completionDate?.toLocaleString() ?? nowMoment().toLocaleString()}`}
					placeholder="Completion date"
					className={styles.fullWidth}
					disabled
					showTime
					showTimezone
					forceUtcUpdate
					defaultValue={(voyageDetails?.completionDate ?
						toMoment(voyageDetails?.completionDate as Moment).clone().utc() : null)}
					onChange={(date) => updateVoyageField('completionDate', date)}
					time
					forceOnChange
				/>
			),
		},
		{
			visible: isTcOut,
			key: 'tcOutCompletionPort',
			label: 'Completion port',
			value: voyageDetails.completionPort?.name ??
				completionEntry?.port?.name ??
				'N/A',
		},
		{
			visible: isTcOut,
			key: 'tcOutCompletionTime',
			label: 'Completion date',
			value: (() => {
				const date = voyageDetails.completionDate ??
					completionEntry?.departureDate ??
					completionEntry?.estimatedDepartureDate;
				const dateMoment = date != null ? toMoment(date) : null;

				return (
					<Flex gap={5}>
						<EstimatedActualTag
							data-testid={'estimated-actual-tc-out-completion-tag'}
							estimated={(matchActionWithEntry.completion ?? completionEntry)?.departureDate == null}
						/>
						<DatePicker
							key={'tc-out-completion-' + dateMoment?.toISOString()}
							placeholder="Completion date"
							className={styles.fullWidth}
							defaultValue={dateMoment}
							time
							disabled
							forceOnChange
							showTime
							showTimezone
							defaultUtcOffset={dateMoment?.utcOffset()}
						/>
					</Flex>
				);
			})(),
		},
		{
			visible: fixedById != null,
			key: 'fixedBy',
			label: 'Fixed By',
			value: userIndex.get(fixedById ?? 0)?.name,
		},
		{
			visible: operatedById != null,
			key: 'operatedBy',
			label: 'Operated By',
			value: userIndex.get(operatedById ?? 0)?.name,
		},
	];

	const theShip = [...(usesTheShip ? [{
		visible: usesTheShip,
		key: '3',
		label: 'TheShip Integration',
		value: voyageDetails.vesselExternalId ? (
			<Badge status="success" text="Connected" />
		) : (
			<Space>
				<Badge status="error" text="Not connected" />
				<Button
					onClick={linkTheShip}
					type="link"
					icon={(<LinkOutlined />)}
				>
					Connect
				</Button>
			</Space>
		),
	}] : [])];

	const filtered = allItems.filter((a) => a.visible);
	const visibleDeliveryFields = deliveryFields.filter((a) => a.visible);
	const visibleRedeliveryFields = redeliveryFields.filter((a) => a.visible);

	const detailsColumn = columns ?? 4;
	// Equal pair layout: with column=4, each item should span 2 so port + date sit 50/50.
	const detailsColSpan = 2;

	return (
		<Flex vertical gap={10}>
			<Details
				column={detailsColumn}
				colSpan={detailsColSpan}
				title={null}
				items={filtered}
				className={styles.details}
				labelWidth={labelWidth}
			/>
			{visibleDeliveryFields.length > 0 && (
				<Details
					column={detailsColumn}
					colSpan={detailsColSpan}
					title={null}
					items={visibleDeliveryFields}
					className={classNames(styles.details, styles.deliveryFields)}
					labelWidth={labelWidth}
					data-testid="delivery-fields"
				/>
			)}
			{visibleRedeliveryFields.length > 0 && (
				<Details
					column={detailsColumn}
					colSpan={detailsColSpan}
					title={null}
					items={visibleRedeliveryFields}
					className={classNames(styles.details, styles.redeliveryFields)}
					labelWidth={labelWidth}
					data-testid="redelivery-fields"
				/>
			)}
			{theShip.length > 0 && (
				<Details
					column={1}
					title={null}
					items={theShip.filter((a) => a.visible)}
					className={styles.details}
					labelWidth={labelWidth}
				/>
			)}
		</Flex>
	);
};

export default SelectItineraryEntryAndDates;
